import {Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild} from "@angular/core";
import {SampleHistoryProcessSteps} from "../models/sampleHistoryProcessSteps";
import {MessagesNewService} from "../messages-new/messages.service";
import {AdminSearchService} from "../admin-search/admin-search/admin-search.service";
import {
    ALLOWED_STATUS_FOR_SAMPLE_TYPE_UPDATE,
    convertUTCToLocal,
    DATES_TO_FORMAT,
    DEFAULT_DATE,
    METADATA_KEYS_FOR_PLATE,
    PLATE_ID_LIST,
    prepareHistoryProcessSteps,
    ProcessStepsForExcelExport
} from "../history/history-utils";
import {MasterDataService} from "../masterdata/masterdata.service";
import {FileSelectEvent, FileUpload} from "primeng/fileupload";
import {PlateUpdateInfo} from "../ma-plate-preparation/multiplate-update-dto";
import {AnnouncedService} from "../apg-announced/services/announced.service";
import {ErrorHandler} from "../utils/error-handler";
import {PlateProcessingService} from "../plate-processing/plate-processing.service";
import {Protocol} from "../protocol-configuration/dto/Protocol";
import {ProjectWorkflow} from "../masterdata/models/projectWorkflow";
import {ConfigurationService} from "../protocol-configuration/configuration.service";
import {forkJoin, Observable} from "rxjs";
import {ProcessStep} from "../models-new/processStep";
import {ReceptionComponentName} from "../constants/ProtocolComponentMapping";
import {Plate} from "../models-new/plate";
import {SourcePlate} from "../models-new/sourcePlate";
import {PlateStatus} from "../constants/Status";
import {Project} from "../masterdata/models/project";
import {HistoryDialogDTO} from "./history-dialog-dto";
import {Scan} from "../protocol-configuration/dto/Scan";

@Component({
    selector: "app-plate-search",
    templateUrl: "./plate-search.component.html",
    styleUrls: ["./plate-search.component.css"],
})
export class PlateSearchComponent implements OnChanges {
    @Input()
    searchInput!: string;
    @Input()
    selectedLabID!: string;
    @Input()
    puid!: string;
    @Input()
    isSuperUserOrAdmin!: boolean;

    @Output()
    onDownloadExcelClick = new EventEmitter<ProcessStepsForExcelExport>();

    plate: Plate = null;
    plateProcessSteps: SampleHistoryProcessSteps[] = [];
    isLoading: boolean = false;
    plateWorkflow: ProjectWorkflow = null;
    plateUpstreamProtocol: Protocol = null;
    plateDownstreamProtocol: Protocol = null;

    platedSampleIDs: string[] = [];
    sourcePlates: string[] = [];
    sampleTypeAndProcessFlowName: Map<string, string> = new Map();

    dropDownRedoListOptions = [];
    selectedRedoOption: any;
    dropDownStatusListOptions = [];
    selectedStatusOption: any;
    dropDownSampleTypeOptions = [];
    selectedSampleTypeOption: any;
    dropDownPlateIDOptions = [];
    selectedPlateIDOption: any;

    redoDialog: boolean = false;
    sampleTypeDialog: boolean = false;
    statusDialog: boolean = false;

    @ViewChild("externalRedoFile")
    externalRedoFile!: FileUpload;
    selectedExternalRedoFile!: File;

    isFileSelected: boolean = false;

    multiUpdatePlateInfos: PlateUpdateInfo[] = [];
    multiUpdatePlateIDs: string[] = [];
    multiUpdateLastUpdatedPlateIDs: string[] = [];

    errorHandler: ErrorHandler = new ErrorHandler();

    showBatchComment: boolean = false;
    searchPlateID: string;
    showPopupWithMultipleSourcePlates = false;
    sourcePlateLists: any;
    private readonly VIEW_ARRAY_LABEL = 'View Array';
    private readonly VIEW_PLATE = 'View Plate';
    plateViewButtonLabel: string;
    selectedPlateHeader: string;
    popupHeader: string;
    redoSelectionMap: any = {};
    searchedPlateMap: any = {};
    deletePlateFromBatch = false;

    selectedProtocol: Protocol = new Protocol();
    projectMap: Map<string, Project> = new Map();
    workflowMap: Map<string, ProjectWorkflow> = new Map();
    selectedProject: Project = new Project();
    historyDialogDTO: HistoryDialogDTO;
    availableRedoList: Array<string> = [];
    readonly BLOCK_PLATE_STATUES: Array<string> = [PlateStatus.NOT_PROCESSED, PlateStatus.RETURNED];

    constructor(
        private readonly messageService: MessagesNewService,
        private readonly adminSearchService: AdminSearchService,
        private readonly masterDataService: MasterDataService,
        private readonly platePreparationService: PlateProcessingService,
        private readonly announcementService: AnnouncedService,
        private readonly configurationService: ConfigurationService
    ) {
    }

    ngOnChanges(_changes: SimpleChanges): void {
        if (!this.searchInput) {
            this.resetValues();
        } else if (this.searchInput && this.searchInput !== "") {
            this.searchPlate(this.searchInput);
        }
    }

    searchPlate(plateID: string): void {
        this.resetValues();
        this.isLoading = true;
        this.plateViewButtonLabel = this.getPlateViewButtonLabel(false);
        this.adminSearchService.fetchPlateByPlateIDandLab(this.selectedLabID, plateID).subscribe({
            next: (parentPlateID) => {
                this.isLoading = false;
                this.searchPlateID = parentPlateID
                this.fetchPlateDetails(parentPlateID);
            },
            error: (_error) => {
                this.isLoading = false;
                this.messageService.addErrorMessage({
                    severity: "error",
                    summary: "Plate not found",
                    detail: `Plate ${plateID} does not exists.`,
                });
                this.resetValues();
            }
        });
    }

    resetValues() {
        this.plate = null;
        this.dropDownRedoListOptions = [];
        this.plateProcessSteps = [];
        this.dropDownStatusListOptions = [];
        this.dropDownPlateIDOptions = [];
        this.redoSelectionMap = {};
        this.searchedPlateMap = {};
        this.plateWorkflow = null;
        this.plateUpstreamProtocol = null;
        this.plateDownstreamProtocol = null;
        this.historyDialogDTO = null;
        this.selectedProtocol = new Protocol();
        this.availableRedoList = [];
    }

    fetchPlateDetails(plateID: string): void {
        this.isLoading = true;
        this.platePreparationService.getPlate(this.selectedLabID, plateID).subscribe({
            next: (plate) => {
                this.isLoading = false;
                this.searchedPlateMap[plate.plateID] = plate
                this.plateViewButtonLabel = this.getPlateViewButtonLabel(plate?.type === 'ARRAY');
                this.populateSampleID(plate.samples);
                this.fetchHistoryDetailsOfPlate(plateID, plate.processSteps);
            },
            error: (_error) => {
                this.isLoading = false;
                this.messageService.addErrorMessage({
                    severity: "error",
                    summary: "Failed to fetch plate information",
                    detail: `Failed to fetch information for the plate: ${plateID}`,
                });
            }
        });
    }

    fetchHistoryDetailsOfPlate(plateID: string, plateProcessSteps: ProcessStep[]) {
        this.isLoading = true;
        this.adminSearchService.getPlateHistory(this.selectedLabID, plateID).subscribe({
            next: (result) => {
                this.isLoading = false;
                this.plate = result.data;
                this.preparePlateSearchUI(plateProcessSteps);
                this.getWorkFlowForPlate(this.plate.workFlow);
                this.getProjects();
            },
            error: (_error) => {
                this.isLoading = false;
                this.messageService.addErrorMessage({
                    severity: "error",
                    summary: "Failed to fetch history details.",
                    detail: `Failed to fetch history details for the plate: ${plateID}`,
                });
                this.resetValues();
            },
        });
    }

    preparePlateSearchUI(plateProcessSteps: ProcessStep[]): void {
        this.plateProcessSteps = prepareHistoryProcessSteps(this.plate.processSteps);
        this.populateSourcePlateIfAny(this.plate.sourcePlates);
        this.populateStatusChangeDropDown(plateProcessSteps, this.plate.processSteps);
        this.populatePlateIDDropDown();
    }

    populateSourcePlateIfAny(sourcePlates: SourcePlate[]): void {
        this.sourcePlates = sourcePlates.map((sourcePlate) => sourcePlate.plateID);
    }

    populatePlateIDDropDown(): void {
        for (let idType of PLATE_ID_LIST) {
            if (!this.plate[idType.value.key]) continue;

            const idTypeForView = {...idType};
            idTypeForView.label = `${idType.label} (${this.plate[idType.value.key]})`;
            this.dropDownPlateIDOptions.push(idTypeForView);
        }
    }

    populateStatusChangeDropDown(processSteps: ProcessStep[], historyProcessSteps: ProcessStep[]) {
        this.dropDownStatusListOptions = [];
        let lastProcessStepReached = false;
        let processStepStatusMap = new Map<string, string>();
        historyProcessSteps.forEach((process) => {
            if (process.step === ReceptionComponentName.QC_SCAN && process.status !== PlateStatus.QCSCAN_STARTED && process.endTimeStamp !== null) {
                processStepStatusMap.clear();
                processStepStatusMap.set(process.status, process.step);
            } else if (process.step !== ReceptionComponentName.QC_SCAN) {
                processStepStatusMap.set(process.status, process.step);
            }
        });

        if(this.plate.type === 'PLATE'){
            this.dropDownStatusListOptions.push({label:PlateStatus.NOT_PROCESSED,value: PlateStatus.NOT_PROCESSED});
            this.dropDownStatusListOptions.push({label:PlateStatus.RETURNED,value: PlateStatus.RETURNED});
        }

        if (processSteps && processSteps.length > 0) {
            let lastProcessStep = processSteps[processSteps.length - 1];

            processStepStatusMap.forEach((step, status) => {
                if (!lastProcessStepReached && step === lastProcessStep.step && status === this.plate.status) {
                    lastProcessStepReached = true;
                    return;
                }
                if (!lastProcessStepReached) {
                    //do not include reception started and completed steps in dropdown if plate is part of a batch
                    if (!this.checkIfBatchIdPresent() || (this.checkIfBatchIdPresent() && !status.includes(ReceptionComponentName.RECEPTION))) {
                        this.dropDownStatusListOptions.push({label: status, value: status});
                    }
                }
            });
        }
    }


    populateSampleID(samples: any[]): void {
        samples.forEach((sample) => {
            this.platedSampleIDs.push(sample._id);
        });
    }

    checkIfValidState(fields: any): boolean {
        const {key, value} = fields;

        if (value == null || value === "" || value === DEFAULT_DATE) {
            return false;
        }

        return METADATA_KEYS_FOR_PLATE.includes(key);
    }

    checkIfBatchIdPresent() {
        if (this.searchedPlateMap[this.plate?.plateID]?.batchID && this.searchedPlateMap[this.plate?.plateID]?.batchID !== null) {
            return true;
        }
        return false;
    }

    updatePlateStatus() {

        this.isLoading = true;
        const idType = this.selectedPlateIDOption.key;
        const updatedPlateID = this.plate[idType];

        const data = {
            labid: this.selectedLabID,
            plate: this.plate.plateID,
            status: this.selectedStatusOption,
            updatedPlateID: updatedPlateID,
            qcNanoDropConfigured: false,
            puid: this.puid,
            removePlate: this.deletePlateFromBatch,
            plateType: this.selectedPlateIDOption.type,
            batchPresent: this.checkIfBatchIdPresent(),
            batchId: this.searchedPlateMap[this.plate?.plateID]?.batchID
        }

        this.platePreparationService.updatePlateSelectedStatusInNewUi(data).subscribe({
            next: (_result) => {
                this.isLoading = false;
                this.searchPlate(this.searchInput);
                this.closeDialog();
                this.messageService.addSuccessMessage({
                    severity: "success",
                    summary: "Success",
                    detail: "Plate status updated successfully",
                });
            },
            error: (_error) => {
                this.isLoading = false;
                this.messageService.addErrorMessage({
                    severity: 'error',
                    summary: 'Plate status update failed!',
                    detail: `Unable to update the plate ${this.plate.lastUpdatedPlateID} to status: ${this.selectedStatusOption}`
                })
                this.closeDialog();
            }
        });
    }

    updatePlateProcessFlowBasedOnSampleType(): void {
        const data = {
            sampleType: this.selectedSampleTypeOption,
            processFlowName: this.sampleTypeAndProcessFlowName.get(
                this.selectedSampleTypeOption
            ),
            plateid: this.plate.plateID,
            labid: this.selectedLabID,
        };

        if (ALLOWED_STATUS_FOR_SAMPLE_TYPE_UPDATE.includes(this.plate.status)) {
            this.messageService.addWarningMessage({
                severity: "warn",
                summary: "Sample type not updated!",
                detail: `Plate has exceeded QC and is not allowed for Sample type update.`,
            });
            this.closeDialog();
            return;
        }

        this.isLoading = true;
        this.platePreparationService.updatePlateProcessflowname(data).subscribe({
            next: (_result) => {
                this.isLoading = false;
                if (this.platedSampleIDs.length !== 0) {
                    this.updateSampleTypeOfSamples();
                }
                this.closeDialog();
            },
            error: (_error) => {
                this.isLoading = false;
                this.messageService.addErrorMessage({
                    severity: "error",
                    summary: "Sample type update failed!",
                    detail: `Unable to update the sample type to ${this.selectedSampleTypeOption} for the given plate.`,
                });
                this.closeDialog();
            },
        });
    }

    updateSampleTypeOfSamples(): void {
        this.isLoading = false;
        this.announcementService.updateAssignedSampleTypeOfSamples(this.selectedLabID, this.platedSampleIDs, this.selectedSampleTypeOption).subscribe({
            next: (_result) => {
                this.isLoading = false;
                this.messageService.addSuccessMessage({
                    severity: "success",
                    summary: "Success",
                    detail:
                        "Sample type updated for all the samples placed on the plate",
                });
            },
            error: (_error) => {
                this.isLoading = false;
                this.messageService.addErrorMessage({
                    severity: "error",
                    summary: "Sample type update failed for samples on the plate!",
                });
                this.closeDialog();
            }
        });
    }

    openRedoDialog(): void {
        this.prepareDataForRedo();
        this.redoDialog = true;
    }

    openStatusDialog(): void {
        this.statusDialog = true;
    }

    openSampleTypeDialog(): void {
        this.sampleTypeDialog = true;
    }

    checkIfPlateIsBatchedAndBeingBlocked(): void {
        //Remove the plate automatically from the batch if status is changed to any one of the blocked states
        this.deletePlateFromBatch = this.checkIfBatchIdPresent() && this.BLOCK_PLATE_STATUES.includes(this.selectedStatusOption);
    }

    closeDialog(): void {
        this.historyDialogDTO = null;
        this.selectedProtocol = new Protocol();
        this.redoSelectionMap = {};
        this.availableRedoList = [];
        this.redoDialog = false;
        this.statusDialog = false;
        this.sampleTypeDialog = false;
        this.selectedRedoOption = undefined;
        this.selectedPlateIDOption = undefined;
        this.selectedSampleTypeOption = undefined;
        this.selectedStatusOption = undefined;
        this.externalRedoFile.clear();
        this.isFileSelected = false;
        this.multiUpdatePlateIDs = [];
        this.multiUpdateLastUpdatedPlateIDs = [];
        this.multiUpdatePlateInfos = [];
        this.deletePlateFromBatch = false;
    }

    requiresFormatting(field): string {
        if (field && field.key === 'sourcePlates' && Array.isArray(field.value)) {
            return field.value.map(sourcePlate => sourcePlate.plateID).join(', ');
        } else {
            return DATES_TO_FORMAT.includes(field.key)
                ? convertUTCToLocal(field.value)
                : field.value;
        }
    }

    emitDownloadEvent(): void {
        const processStepsForExcelExport: ProcessStepsForExcelExport = {
            name: this.plate.lastUpdatedPlateID,
            processSteps: this.plateProcessSteps,
        };
        this.onDownloadExcelClick.emit(processStepsForExcelExport);
    }

    onFileSelect(event: FileSelectEvent): void {
        if (event.files.length === 0) {
            this.isFileSelected = false;
            return;
        }
        this.isFileSelected = true;
        this.selectedExternalRedoFile = event.files[0];
    }

    showError(error: any): void {
        const message = this.errorHandler.handleError(error);
        this.messageService.addErrorMessage(message);
    }

    getCommentsDialogHeader() {
        return `${this.searchInput} comments`;
    }

    getPlateViewButtonLabel(plateView) {
        return plateView ? this.VIEW_PLATE : this.VIEW_ARRAY_LABEL;
    }

    getSearchPlateInfo() {
        this.sourcePlateLists = this.plate.sourcePlates.map(plate => ({plateID: plate.plateID}));
        if (this.sourcePlates.length === 0) {
            this.getBeadChipsByPlateIDandLab();
        } else {
            this.getPlateIDByLabAndBeadChip()
        }
    }

    getPlateIDByLabAndBeadChip() {
        if (this.sourcePlates.length > 1) {
            this.popupHeader = "Select Plate";
            this.selectedPlateHeader = "Plate ID";
            this.showPopupWithMultipleSourcePlates = true;
        } else if (this.sourcePlates.length === 1) {
            this.plateViewButtonLabel = this.getPlateViewButtonLabel(false);
            this.searchPlate(this.sourcePlates[0])
        }
    }

    getBeadChipsByPlateIDandLab() {
        this.isLoading = true;
        this.adminSearchService.getBeadChipsByPlateIDandLab(this.selectedLabID, this.searchPlateID).subscribe({
            next: (sourcePlates) => {
                this.isLoading = false;
                this.sourcePlateLists = sourcePlates.map(plate => ({plateID: plate.plateID}));
                if (sourcePlates.length > 1) {
                    this.popupHeader = "Select Array";
                    this.selectedPlateHeader = "Array ID";
                    this.showPopupWithMultipleSourcePlates = true;
                } else if (sourcePlates.length === 1) {
                    this.plateViewButtonLabel = this.getPlateViewButtonLabel(true);
                    this.fetchHistoryDetailsOfPlate(sourcePlates[0].plateID, sourcePlates[0].processSteps);
                }
            },
            error: (_error) => {
                this.isLoading = false;
                this.messageService.addWarningMessage({
                    severity: "warn",
                    summary: "Array not found",
                    detail: `${this.searchPlateID} does not have any Arrays.`,
                });
            }
        })
    }

    onPlateSelect(plate) {
        this.plateViewButtonLabel = this.getPlateViewButtonLabel(this.plateViewButtonLabel === this.VIEW_ARRAY_LABEL);
        this.fetchHistoryDetailsOfPlate(plate.plateID, plate.processSteps);
        this.showPopupWithMultipleSourcePlates = false;
    }

    getWorkFlowForPlate(workflowName: string) {
        this.isLoading = true;
        this.masterDataService.getConfigList(this.selectedLabID, "projectWorkflow", 0, 1000, true).subscribe({
            next: (results) => {
                this.isLoading = false;
                const workFlowData = results.data;
                workFlowData.forEach((workflow: ProjectWorkflow) => {
                    this.workflowMap.set(`${workflow.projectId}_${workflow.workflowName}`, workflow)
                })
                this.plateWorkflow = workFlowData.find(workflow => workflow.workflowName === workflowName)
                const upstreamProtocol$ = this.getPlateProtocol(this.plateWorkflow.upstream[0].protocolName, this.plateWorkflow.upstream[0].protocolVersion, "upstream")
                const downstreamProtocol$ = this.getPlateProtocol(this.plateWorkflow.downstream[0].protocolName, this.plateWorkflow.downstream[0].protocolVersion, "downstream")

                forkJoin([upstreamProtocol$, downstreamProtocol$]).subscribe({
                    next: () => {
                        this.populateRedoList();
                    },
                    error: (error) => {
                        this.showError(error);
                    }
                })
            },
            error: (error: any) => {
                this.isLoading = false;
                this.showError(error);
            }
        });
    }

    getProjects() {
        this.masterDataService.getConfigList(this.selectedLabID, "project", 0, 1000, true).subscribe({
            next: (results) => {
                if (!results) {
                    return;
                }
                const activeProjectsList = results.data;
                this.projectMap.clear();
                activeProjectsList.forEach((project: Project) => {
                    if (project.projectName === this.plate.projectName) {
                        this.selectedProject = project;
                    }
                    this.projectMap.set(project.projectId, project)
                })
            },
            error: (error) => {
                this.messageService.addErrorMessage({
                    severity: "error", summary: "Error while fetching active projects",
                    detail: error.error.bbApiError.message
                })
            }
        });
    }

    getPlateProtocol(protocolName: string, protocolVersion: number, type: string) {
        return new Observable<void>((observer) => {
            this.isLoading = true;
            this.configurationService.getProtocol(this.selectedLabID, protocolName, protocolVersion).subscribe({
                next: result => {
                    this.isLoading = false;
                    if (JSON.stringify(result) === "{}") {
                        this.messageService.addErrorMessage({
                            severity: "error", summary: "Plate protocol not found",
                            detail: `Protocol ${protocolName}(v${protocolVersion}) not found for plate ${this.plate.lastUpdatedPlateID}`
                        })
                        return;
                    }
                    this.setProtocolForPlate(type, result);
                    observer.next();
                    observer.complete();
                },
                error: error => {
                    this.isLoading = false;
                    this.messageService.addErrorMessage({
                        severity: 'error', summary: 'Error Occurred!',
                        detail: 'Error occurred while fetching protocol config! ' + error.error.bbApiError.message
                    })
                    observer.error();
                }
            })
        })
    }

    private setProtocolForPlate(type: string, result: any) {
        if (type === "upstream") {
            this.plateUpstreamProtocol = result.data;
            Object.keys(this.plateUpstreamProtocol.redoConfigurationMap).forEach(key => {
                this.setChangeToStatusForReceptionOrQCCompleted(key, this.plateUpstreamProtocol)
            });
        } else if (type === "downstream") {
            this.plateDownstreamProtocol = result.data;
            Object.keys(this.plateDownstreamProtocol.redoConfigurationMap).forEach(key => {
                this.setChangeToStatusForReceptionOrQCCompleted(key, this.plateDownstreamProtocol)
            });
        }
    }

    private setChangeToStatusForReceptionOrQCCompleted(key: string, protocol: Protocol) {
        const redoConfig = protocol.redoConfigurationMap[key];
        if (redoConfig['changeToStatus'] === "RECEPTION_COMPLETED/QCSCAN_COMPLETED") {
            if (this.plateWorkflow.qcScan) {
                redoConfig['changeToStep'] = 'QC_SCAN';
                redoConfig['changeToStatus'] = 'QCSCAN_COMPLETED'
            } else {
                redoConfig['changeToStep'] = 'RECEIVE_PLATE';
                redoConfig['changeToStatus'] = 'RECEPTION_COMPLETED'
            }
        }
    }


    populateRedoList() {
        const plateStepActionList = this.plateProcessSteps.map(step => step.stepAction)
        const extractionRedoMap = this.plateUpstreamProtocol.redoConfigurationMap;
        const plateRedoMap = this.plateDownstreamProtocol.redoConfigurationMap;
        const redoMap = {...extractionRedoMap, ...plateRedoMap}
        const redoList: any[] = [];

        Object.values(redoMap).forEach((redoConfig) => {
            if (plateStepActionList.includes(redoConfig['changeToStatus'])) {
                redoList.push(
                    {
                        label: redoConfig['redoConfigurationName'],
                        value: {
                            changeToPlateIDType: redoConfig['changeToPlateIDType'],
                            changeToStatus: redoConfig['changeToStatus'],
                            changeToStep: redoConfig['changeToStep'],
                            changeToStepPosition: redoConfig['changeToStepPosition'],
                            redoConfigurationName: redoConfig['redoConfigurationName'],
                            setSingleSampleRedo: redoConfig['setSingleSampleRedo'],
                        }
                    }
                )
            }
        })
        this.dropDownRedoListOptions = redoList
    }

    prepareDataForRedo() {
        const stepName = this.plate.processSteps[this.plate.processSteps.length - 1].step;
        if (this.plateUpstreamProtocol?.protocolExtractionStepList.includes(stepName)) {
            this.selectedProtocol = this.plateUpstreamProtocol;
        } else {
            this.selectedProtocol = this.plateDownstreamProtocol;
        }
        Object.entries(this.selectedProtocol.redoConfigurationMap).forEach(([_key, value]) => {
            const config = value as { redoConfigurationName: string };
            this.availableRedoList.push(config.redoConfigurationName);
        });

        const scan: Scan = new Scan();
        scan.plateType = "SOURCE";
        scan.scanType = "Scan SOURCE Plate";
        scan.scanName = "PLATE";
        this.historyDialogDTO = {
            "protocolStepName": stepName,
            "scannedPlateID": this.plate.plateID,
            "dialogName": "Redo Plate",
            "componentPopupTitle": "Redo Plate",
            "isRedoFromHistory": true,
            "scans": [scan],
            "availableRedoList": this.availableRedoList
        };
    }

    checkIfPlateIsBlocked(): boolean {
        return this.BLOCK_PLATE_STATUES.includes(this.plate?.status);
    }
}
